import type { PdfLetterheadSettings } from "../../../../clients/graphqlTypes.js";

/**
 * Initial margin/color values handed to `LetterheadSettingsForm`. Every field is
 * optional: a missing field lets the form fall back to its own brand defaults
 * (e.g. `#001e64`, `50mm`). This is the single source of truth shared by the
 * edit and create paths so they cannot diverge (see MEIS-12638).
 */
export interface LetterheadFormInitialSettings {
  marginTopFirstPage?: string;
  marginTopSubsequentPage?: string;
  marginBottom?: string;
  marginLeft?: string;
  marginRight?: string;
  primaryColor?: string;
  secondaryColor?: string;
  textPrimaryColor?: string;
  textSecondaryColor?: string;
}

/**
 * Resolves the settings the letterhead form should start from.
 *
 * - `existing` present → start from the organisation's stored letterhead
 *   (margins + 4 colors), so creating/editing never silently resets to defaults.
 * - `generated` text colors (chosen in the "Generate" step) override the text
 *   colors, so the user's just-made selection survives into the settings form.
 * - neither present → all fields stay `undefined` and the form applies its own
 *   brand defaults (correct for a true first-time creation).
 */
export function resolveLetterheadFormInitialSettings({
  existing,
  generated,
}: {
  existing: PdfLetterheadSettings | null | undefined;
  generated?: {
    textPrimaryColor?: string | null;
    textSecondaryColor?: string | null;
  } | null;
}): LetterheadFormInitialSettings {
  const base: LetterheadFormInitialSettings = existing
    ? {
        marginTopFirstPage: existing.marginTopFirstPage,
        marginTopSubsequentPage: existing.marginTopSubsequentPage,
        marginBottom: existing.marginBottom,
        marginLeft: existing.marginLeft,
        marginRight: existing.marginRight,
        primaryColor: existing.primaryColor,
        secondaryColor: existing.secondaryColor,
        textPrimaryColor: existing.textPrimaryColor,
        textSecondaryColor: existing.textSecondaryColor,
      }
    : {};

  return {
    ...base,
    textPrimaryColor: generated?.textPrimaryColor ?? base.textPrimaryColor,
    textSecondaryColor:
      generated?.textSecondaryColor ?? base.textSecondaryColor,
  };
}
